import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Group,
  Modal,
  Pagination,
  Paper,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  TextInput,
  Textarea,
  Title,
  Tooltip,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  IconAlertCircle,
  IconBrandWhatsapp,
  IconPhone,
  IconPlus,
  IconSearch,
} from '@tabler/icons-react';
import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { api } from '../api';
import { SupplierSectionNav } from '../components/supplier-section-nav';
import { phoneHref, whatsappHref } from '../features/suppliers/supplier-utils';
import { apiHook } from '../treaty';

function errorMessage(error: unknown) {
  if (typeof error === 'object' && error && 'value' in error) {
    const value = (error as { value?: unknown }).value;
    if (typeof value === 'object' && value && 'message' in value) return String(value.message);
  }
  return 'The supplier could not be saved.';
}

type DuplicateCandidate = { id: string; name: string; reasons: string[] };

function duplicateCandidatesFrom(error: unknown): DuplicateCandidate[] {
  if (typeof error !== 'object' || !error || !('value' in error)) return [];
  const value = (error as { value?: unknown }).value;
  if (typeof value !== 'object' || !value || !('details' in value)) return [];
  const details = (value as { details?: unknown }).details;
  if (typeof details !== 'object' || !details || !('candidates' in details)) return [];
  const candidates = (details as { candidates?: unknown }).candidates;
  if (!Array.isArray(candidates)) return [];
  return candidates.filter(
    (candidate): candidate is DuplicateCandidate =>
      typeof candidate === 'object' &&
      candidate !== null &&
      'id' in candidate &&
      typeof candidate.id === 'string' &&
      'name' in candidate &&
      typeof candidate.name === 'string' &&
      'reasons' in candidate &&
      Array.isArray(candidate.reasons),
  );
}

export function SuppliersPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [opened, modal] = useDisclosure(false);
  const [search, setSearch] = useState('');
  const [statusId, setStatusId] = useState<string | null>(null);
  const [followUp, setFollowUp] = useState<string | null>(null);
  const [contactState, setContactState] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const mastersQuery = apiHook.useQuery((client) => client['supplier-master-data'].get());
  const suppliersQuery = apiHook.useQuery((client) =>
    client.suppliers.get({
      query: {
        ...(search.trim() ? { search: search.trim() } : {}),
        ...(statusId ? { statusId } : {}),
        ...(followUp ? { followUp: followUp as 'overdue' | 'today' | 'upcoming' | 'none' } : {}),
        ...(contactState ? { contactState: contactState as 'contacted' | 'never' } : {}),
        page,
        pageSize: 25,
      },
    }),
  );
  const [name, setName] = useState('');
  const [sourceTypeId, setSourceTypeId] = useState<string | null>(null);
  const [sourceUrl, setSourceUrl] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [duplicateCandidates, setDuplicateCandidates] = useState<DuplicateCandidate[]>([]);

  function resetForm() {
    setName('');
    setSourceTypeId(null);
    setSourceUrl('');
    setPhone('');
    setEmail('');
    setCity('');
    setState('');
    setNotes('');
    setDuplicateCandidates([]);
  }

  async function saveSupplier(allowPossibleDuplicate = false) {
    if (!name.trim() || !sourceTypeId) {
      notifications.show({
        color: 'red',
        message: 'Supplier name and discovery source are required.',
      });
      return;
    }
    setSaving(true);
    const response = await api.api.api.v1.suppliers.post({
      name,
      sourceTypeId,
      ...(sourceUrl.trim() ? { sourceUrl } : {}),
      ...(phone.trim() ? { phone } : {}),
      ...(email.trim() ? { email } : {}),
      ...(city.trim() || state.trim()
        ? {
            location: {
              label: 'Primary',
              type: 'office' as const,
              ...(city.trim() ? { city } : {}),
              ...(state.trim() ? { state } : {}),
            },
          }
        : {}),
      ...(notes.trim() ? { notes } : {}),
      ...(allowPossibleDuplicate ? { allowPossibleDuplicate: true } : {}),
    });
    setSaving(false);
    if (response.error) {
      if (response.status === 409 && !allowPossibleDuplicate) {
        setDuplicateCandidates(duplicateCandidatesFrom(response.error));
        return;
      }
      notifications.show({ color: 'red', message: errorMessage(response.error) });
      return;
    }
    resetForm();
    modal.close();
    await queryClient.invalidateQueries({ queryKey: ['suppliers'] });
    notifications.show({ color: 'green', message: 'Supplier added.' });
    if (response.data) void navigate(`/suppliers/${response.data.id}`);
  }

  const masters = mastersQuery.data;
  const result = suppliersQuery.data;
  return (
    <Stack gap='lg' maw={1500} mx='auto'>
      <Group justify='space-between' align='end'>
        <div>
          <Title order={1}>Supplier sourcing</Title>
          <Text c='dimmed' mt={5}>
            Capture suppliers, conversations, follow-ups, products, and price history.
          </Text>
        </div>
        <Button leftSection={<IconPlus size={17} />} onClick={modal.open}>
          Add supplier
        </Button>
      </Group>
      <SupplierSectionNav />

      <Paper withBorder radius='lg' p='md'>
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }}>
          <TextInput
            leftSection={<IconSearch size={16} />}
            placeholder='Search supplier, contact, phone, city or product'
            value={search}
            onChange={(event) => {
              setSearch(event.currentTarget.value);
              setPage(1);
            }}
          />
          <Select
            clearable
            searchable
            placeholder='All statuses'
            data={(masters?.statuses ?? []).map((status) => ({
              value: status.id,
              label: status.name,
            }))}
            value={statusId}
            onChange={(value) => {
              setStatusId(value);
              setPage(1);
            }}
          />
          <Select
            clearable
            placeholder='Any follow-up'
            data={[
              { value: 'overdue', label: 'Overdue' },
              { value: 'today', label: 'Due today' },
              { value: 'upcoming', label: 'Upcoming' },
              { value: 'none', label: 'No follow-up' },
            ]}
            value={followUp}
            onChange={(value) => {
              setFollowUp(value);
              setPage(1);
            }}
          />
          <Select
            clearable
            placeholder='Any contact state'
            data={[
              { value: 'never', label: 'Never contacted' },
              { value: 'contacted', label: 'Contacted' },
            ]}
            value={contactState}
            onChange={(value) => {
              setContactState(value);
              setPage(1);
            }}
          />
        </SimpleGrid>
      </Paper>

      {suppliersQuery.isError && (
        <Alert color='red' icon={<IconAlertCircle size={18} />}>
          Suppliers could not be loaded.
        </Alert>
      )}
      <Paper withBorder radius='lg' className='data-card'>
        <Table.ScrollContainer minWidth={1080}>
          <Table highlightOnHover verticalSpacing='md' horizontalSpacing='lg'>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Supplier</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Products</Table.Th>
                <Table.Th>Location</Table.Th>
                <Table.Th>Last contact</Table.Th>
                <Table.Th>Next follow-up</Table.Th>
                <Table.Th ta='right'>Contact</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {suppliersQuery.isLoading && (
                <Table.Tr>
                  <Table.Td colSpan={7}>
                    <Text c='dimmed' ta='center' py='xl'>
                      Loading suppliers…
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
              {!suppliersQuery.isLoading && !result?.items.length && (
                <Table.Tr>
                  <Table.Td colSpan={7}>
                    <Text c='dimmed' ta='center' py='xl'>
                      No suppliers match these filters.
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
              {result?.items.map((supplier) => {
                const channels = supplier.primaryContact?.channels ?? supplier.companyChannels;
                const phone = channels.find((channel: any) => channel.type === 'phone');
                const whatsapp = channels.find((channel: any) => channel.type === 'whatsapp');
                const overdue =
                  supplier.nextFollowUp && new Date(supplier.nextFollowUp.dueAt) < new Date();
                return (
                  <Table.Tr
                    key={supplier.id}
                    className='supplier-row'
                    onClick={() => void navigate(`/suppliers/${supplier.id}`)}
                  >
                    <Table.Td>
                      <Text fw={650}>{supplier.name}</Text>
                      <Group gap={4} mt={4}>
                        {supplier.tags.slice(0, 3).map((tag) => (
                          <Badge key={tag.id} size='xs' color={tag.color} variant='light'>
                            {tag.name}
                          </Badge>
                        ))}
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Badge color={supplier.status.color} variant='light'>
                        {supplier.status.name}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Text size='sm' lineClamp={2}>
                        {supplier.products.map((product) => product.name).join(', ') || '—'}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size='sm'>
                        {[supplier.primaryLocation?.city, supplier.primaryLocation?.state]
                          .filter(Boolean)
                          .join(', ') || '—'}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      {supplier.lastContact ? (
                        <>
                          <Text size='sm'>{supplier.lastContact.activityType.name}</Text>
                          <Text size='xs' c='dimmed'>
                            {dayjs(supplier.lastContact.occurredAt).format('DD MMM YYYY')}
                          </Text>
                        </>
                      ) : (
                        <Badge color='gray' variant='outline'>
                          Never
                        </Badge>
                      )}
                    </Table.Td>
                    <Table.Td>
                      {supplier.nextFollowUp ? (
                        <Text size='sm' c={overdue ? 'red' : undefined} fw={overdue ? 650 : 400}>
                          {dayjs(supplier.nextFollowUp.dueAt).format('DD MMM, HH:mm')}
                        </Text>
                      ) : (
                        '—'
                      )}
                    </Table.Td>
                    <Table.Td ta='right'>
                      <Group gap={4} justify='flex-end' wrap='nowrap'>
                        {phone && (
                          <Tooltip label='Call'>
                            <ActionIcon
                              component='a'
                              href={phoneHref(phone.value)}
                              variant='light'
                              onClick={(event) => event.stopPropagation()}
                            >
                              <IconPhone size={16} />
                            </ActionIcon>
                          </Tooltip>
                        )}
                        {whatsapp && (
                          <Tooltip label='WhatsApp'>
                            <ActionIcon
                              component='a'
                              href={whatsappHref(whatsapp.value)}
                              target='_blank'
                              color='green'
                              variant='light'
                              onClick={(event) => event.stopPropagation()}
                            >
                              <IconBrandWhatsapp size={16} />
                            </ActionIcon>
                          </Tooltip>
                        )}
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
        {result && result.total > result.pageSize && (
          <Group justify='center' p='md'>
            <Pagination
              total={Math.ceil(result.total / result.pageSize)}
              value={page}
              onChange={setPage}
            />
          </Group>
        )}
      </Paper>

      <Modal opened={opened} onClose={modal.close} title='Add supplier' size='lg'>
        <Stack>
          {duplicateCandidates.length > 0 && (
            <Alert color='orange' title='Possible duplicate supplier'>
              <Text size='sm' mb='xs'>
                Review these matches before creating another record:
              </Text>
              <Stack gap={4}>
                {duplicateCandidates.map((candidate) => (
                  <Text key={candidate.id} size='sm'>
                    <b>{candidate.name}</b> — {candidate.reasons.join(', ')}
                  </Text>
                ))}
              </Stack>
            </Alert>
          )}
          <TextInput
            required
            label='Supplier name'
            value={name}
            onChange={(event) => setName(event.currentTarget.value)}
            autoFocus
          />
          <Select
            required
            searchable
            label='Found through'
            placeholder='Choose source'
            data={(masters?.sourceTypes ?? []).map((source) => ({
              value: source.id,
              label: source.name,
            }))}
            value={sourceTypeId}
            onChange={setSourceTypeId}
          />
          <TextInput
            label='Source URL'
            placeholder='Google Maps or IndiaMART link'
            value={sourceUrl}
            onChange={(event) => setSourceUrl(event.currentTarget.value)}
          />
          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            <TextInput
              label='Phone'
              value={phone}
              onChange={(event) => setPhone(event.currentTarget.value)}
            />
            <TextInput
              label='Email'
              value={email}
              onChange={(event) => setEmail(event.currentTarget.value)}
            />
          </SimpleGrid>
          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            <TextInput
              label='City'
              value={city}
              onChange={(event) => setCity(event.currentTarget.value)}
            />
            <TextInput
              label='State'
              value={state}
              onChange={(event) => setState(event.currentTarget.value)}
            />
          </SimpleGrid>
          <Textarea
            label='Notes'
            autosize
            minRows={2}
            value={notes}
            onChange={(event) => setNotes(event.currentTarget.value)}
          />
          <Group justify='flex-end'>
            <Button variant='default' onClick={modal.close}>
              Cancel
            </Button>
            <Button
              loading={saving}
              color={duplicateCandidates.length ? 'orange' : undefined}
              onClick={() => void saveSupplier(duplicateCandidates.length > 0)}
            >
              {duplicateCandidates.length ? 'Create anyway' : 'Save supplier'}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
